import React, { Component } from 'react';
import { flowRight as compose } from 'lodash';
import { withQuery, withMutation } from '../../../hoc/withGraphql';
import getWorkerTierConfig from './getWorkerTierConfig.graphql';
import getWorkerChallenges from './getWorkerChallenges.graphql';
import saveWorkerTierConfigM from './saveWorkerTierConfig.graphql';
import saveWorkerChallengeM from './saveWorkerChallenge.graphql';
import getWorkerPortfolioAdmin from './getWorkerPortfolioAdmin.graphql';
import getInstantPayConfig from './getInstantPayConfig.graphql';
import getInstantPayouts from './getInstantPayouts.graphql';
import saveInstantPayConfigM from './saveInstantPayConfig.graphql';
import setInstantSuspendM from './setInstantSuspend.graphql';
import getOnboardingConfig from './getOnboardingConfig.graphql';
import saveOnboardingConfigM from './saveOnboardingConfig.graphql';

const RED = '#D32F2F';
const INK = '#1F2733';
const TEAL = '#0E9488';
const METRICS = [
  { v: 'completedJobs', l: 'Completed jobs' },
  { v: 'acceptanceRate', l: 'Acceptance rate (%)' },
  { v: 'rating', l: 'Average rating' },
];

// P3: admin Worker Performance — tier thresholds (Bronze→Argent→Or→Platine) + challenges CRUD.
class WorkerPerformance extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tab: 'tiers', busy: false, message: '',
      cfg: null, // parsed tier config
      ipCfg: null, // parsed instant-pay config
      obCfg: null, // parsed onboarding config
      // challenge form
      cId: null, cTitle: '', cDesc: '', cMetric: 'completedJobs', cTarget: '', cReward: '', cStatus: 'active',
      // worker lookup
      wId: '', wLoaded: false,
      // instant pay suspend
      sId: '',
    };
  }

  setIp = (field, val) => {
    const ipCfg = { ...this.state.ipCfg, [field]: parseFloat(val) || 0 };
    this.setState({ ipCfg });
  };

  saveIp = async () => {
    this.setState({ busy: true, message: '' });
    try {
      const res = await this.props.saveIpMut({ variables: { config: JSON.stringify(this.state.ipCfg) } });
      const r = res && res.data && res.data.saveInstantPayConfig;
      this.setState({ busy: false, message: r && r.status === 200 ? '✅ Instant Pay rules saved.' : ((r && r.errorMessage) || 'Save failed.') });
    } catch (e) { this.setState({ busy: false, message: e.message }); }
  };

  toggleSuspend = async (suspended) => {
    if (!this.state.sId) { this.setState({ message: 'Enter a worker ID.' }); return; }
    this.setState({ busy: true, message: '' });
    try {
      const res = await this.props.suspendMut({ variables: { userId: this.state.sId.trim(), suspended } });
      const r = res && res.data && res.data.setInstantPayoutSuspended;
      this.setState({ busy: false, message: r && r.status === 200 ? (suspended ? '✅ Worker Instant Pay suspended.' : '✅ Worker Instant Pay re-enabled.') : ((r && r.errorMessage) || 'Failed.') });
    } catch (e) { this.setState({ busy: false, message: e.message }); }
  };

  lookupWorker = async () => {
    if (!this.state.wId) { this.setState({ message: 'Enter a worker ID.' }); return; }
    this.setState({ busy: true, message: '' });
    try {
      await this.props.portfolio.refetch({ workerId: this.state.wId.trim() });
      this.setState({ busy: false, wLoaded: true });
    } catch (e) { this.setState({ busy: false, message: e.message }); }
  };

  componentDidUpdate(prevProps) {
    const c = this.props.tierCfg && this.props.tierCfg.getWorkerTierConfig;
    if (!this.state.cfg && c && c.status === 200 && c.config) {
      try { this.setState({ cfg: JSON.parse(c.config) }); } catch (e) {}
    }
    const ic = this.props.ipCfg && this.props.ipCfg.getInstantPayConfig;
    if (!this.state.ipCfg && ic && ic.status === 200 && ic.config) {
      try { this.setState({ ipCfg: JSON.parse(ic.config) }); } catch (e) {}
    }
    const oc = this.props.obCfg && this.props.obCfg.getOnboardingConfig;
    if (!this.state.obCfg && oc && oc.status === 200 && oc.config) {
      try { const p = JSON.parse(oc.config); this.setState({ obCfg: { enabled: !!p.enabled, sessions: Array.isArray(p.sessions) ? p.sessions : [] } }); } catch (e) {}
    }
  }

  setObSession = (i, field, val) => {
    const sessions = this.state.obCfg.sessions.map((s, idx) => idx === i ? { ...s, [field]: val } : s);
    this.setState({ obCfg: { ...this.state.obCfg, sessions } });
  };
  addObSession = () => this.setState({ obCfg: { ...this.state.obCfg, sessions: [...this.state.obCfg.sessions, { title: 'BestHand Onboarding', day: 'Tuesday', time: '15:00', timezone: 'ET', zoomLink: '' }] } });
  removeObSession = (i) => this.setState({ obCfg: { ...this.state.obCfg, sessions: this.state.obCfg.sessions.filter((_, idx) => idx !== i) } });

  saveOb = async () => {
    this.setState({ busy: true, message: '' });
    try {
      const res = await this.props.saveObMut({ variables: { config: JSON.stringify(this.state.obCfg) } });
      const r = res && res.data && res.data.saveOnboardingConfig;
      this.setState({ busy: false, message: r && r.status === 200 ? '✅ Onboarding settings saved.' : ((r && r.errorMessage) || 'Save failed.') });
    } catch (e) { this.setState({ busy: false, message: e.message }); }
  };

  setTier = (tier, field, val) => {
    const cfg = { ...this.state.cfg };
    cfg[tier] = { ...cfg[tier], [field]: parseFloat(val) || 0 };
    this.setState({ cfg });
  };

  saveTiers = async () => {
    this.setState({ busy: true, message: '' });
    try {
      const res = await this.props.saveTierMut({ variables: { config: JSON.stringify(this.state.cfg) } });
      const r = res && res.data && res.data.saveWorkerTierConfig;
      this.setState({ busy: false, message: r && r.status === 200 ? '✅ Tier thresholds saved.' : ((r && r.errorMessage) || 'Save failed.') });
    } catch (e) { this.setState({ busy: false, message: e.message }); }
  };

  editChallenge = (c) => this.setState({ cId: c.id, cTitle: c.title || '', cDesc: c.description || '', cMetric: c.metric || 'completedJobs', cTarget: String(c.target || ''), cReward: c.rewardText || '', cStatus: c.status || 'active' });
  resetChallenge = () => this.setState({ cId: null, cTitle: '', cDesc: '', cMetric: 'completedJobs', cTarget: '', cReward: '', cStatus: 'active' });

  saveChallenge = async () => {
    if (!this.state.cTitle) { this.setState({ message: 'Challenge title is required.' }); return; }
    this.setState({ busy: true, message: '' });
    try {
      const res = await this.props.saveChallengeMut({ variables: {
        id: this.state.cId, title: this.state.cTitle, description: this.state.cDesc, metric: this.state.cMetric,
        target: parseFloat(this.state.cTarget) || 0, rewardText: this.state.cReward, status: this.state.cStatus,
      } });
      const r = res && res.data && res.data.saveWorkerChallenge;
      if (r && r.status === 200) { this.resetChallenge(); this.setState({ busy: false, message: '✅ Saved.' }); this.props.challenges.refetch(); }
      else this.setState({ busy: false, message: (r && r.errorMessage) || 'Save failed.' });
    } catch (e) { this.setState({ busy: false, message: e.message }); }
  };

  toggleChallenge = async (c) => {
    this.setState({ busy: true, message: '' });
    try {
      await this.props.saveChallengeMut({ variables: { id: c.id, title: c.title, description: c.description, metric: c.metric, target: c.target, rewardText: c.rewardText, status: c.status === 'active' ? 'closed' : 'active' } });
      this.setState({ busy: false }); this.props.challenges.refetch();
    } catch (e) { this.setState({ busy: false, message: e.message }); }
  };

  render() {
    const input = { padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, marginRight: 8 };
    const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', marginRight: 8 });
    const tabBtn = (t) => ({ ...btn(this.state.tab === t ? RED : '#fff'), color: this.state.tab === t ? '#fff' : INK, border: this.state.tab === t ? 'none' : '1px solid #ddd' });
    const card = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16, marginBottom: 16 };
    const th = { padding: 10, textAlign: 'left' }; const td = { padding: 10, borderTop: '1px solid #f0f0f0', fontSize: 13 };
    const cfg = this.state.cfg;
    const challenges = (this.props.challenges && this.props.challenges.getWorkerChallenges && this.props.challenges.getWorkerChallenges.result) || [];

    const tierRow = (key, label) => cfg && (
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: 700, color: TEAL, marginBottom: 4 }}>{label}</div>
        <label style={{ fontSize: 12, color: '#666' }}>Min completed jobs </label>
        <input style={{ ...input, width: 90 }} value={cfg[key].minCompleted} onChange={(e) => this.setTier(key, 'minCompleted', e.target.value)} />
        <label style={{ fontSize: 12, color: '#666' }}>Min rating </label>
        <input style={{ ...input, width: 70 }} value={cfg[key].minRating} onChange={(e) => this.setTier(key, 'minRating', e.target.value)} />
      </div>
    );

    return (
      <div style={{ fontFamily: 'Poppins, sans-serif' }}>
        <h1 className="siteAdminPrimaryHeading">Worker Performance</h1>
        <div style={{ marginBottom: 16 }}>
          <button style={tabBtn('tiers')} onClick={() => this.setState({ tab: 'tiers', message: '' })}>Tier thresholds</button>
          <button style={tabBtn('challenges')} onClick={() => this.setState({ tab: 'challenges', message: '' })}>Challenges</button>
          <button style={tabBtn('workers')} onClick={() => this.setState({ tab: 'workers', message: '' })}>Worker portfolio</button>
          <button style={tabBtn('instant')} onClick={() => this.setState({ tab: 'instant', message: '' })}>Instant Pay</button>
          <button style={tabBtn('onboarding')} onClick={() => this.setState({ tab: 'onboarding', message: '' })}>Onboarding</button>
          {this.state.message ? <span style={{ marginLeft: 12, color: this.state.message[0] === '✅' ? TEAL : RED, fontWeight: 600 }}>{this.state.message}</span> : null}
        </div>

        {this.state.tab === 'tiers' && (
          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 12 }}>Promotion thresholds (Bronze is the floor; everyone starts Bronze)</div>
            {!cfg ? <div>Loading…</div> : (
              <div>
                {tierRow('argent', 'Argent (Silver)')}
                {tierRow('or', 'Or (Gold)')}
                {tierRow('platine', 'Platine (Platinum)')}
                <div style={{ marginTop: 14 }}>
                  <button style={btn(RED)} disabled={this.state.busy} onClick={this.saveTiers}>Save thresholds</button>
                </div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>A worker reaches the highest tier whose 2 thresholds (completed jobs + rating) are both met. The tier is a neutral quality/experience ladder — it does NOT use acceptance or cancellation rate. Recomputed after each completed/cancelled job.</div>
              </div>
            )}
          </div>
        )}

        {this.state.tab === 'challenges' && (
          <div>
            <div style={card}>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>{this.state.cId ? 'Edit challenge' : 'New challenge'}</div>
              <input style={{ ...input, width: 300 }} placeholder="Title" value={this.state.cTitle} onChange={(e) => this.setState({ cTitle: e.target.value })} />
              <select style={input} value={this.state.cMetric} onChange={(e) => this.setState({ cMetric: e.target.value })}>
                {METRICS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
              </select>
              <input style={{ ...input, width: 110 }} placeholder="Target" value={this.state.cTarget} onChange={(e) => this.setState({ cTarget: e.target.value.replace(/[^0-9.]/g, '') })} />
              <div style={{ marginTop: 8 }}>
                <input style={{ ...input, width: 420 }} placeholder="Description" value={this.state.cDesc} onChange={(e) => this.setState({ cDesc: e.target.value })} />
                <input style={{ ...input, width: 260 }} placeholder="Reward (text)" value={this.state.cReward} onChange={(e) => this.setState({ cReward: e.target.value })} />
              </div>
              <div style={{ marginTop: 10 }}>
                <button style={btn(RED)} disabled={this.state.busy} onClick={this.saveChallenge}>{this.state.cId ? 'Update' : 'Create'}</button>
                {this.state.cId ? <button style={{ ...btn('#fff'), color: INK, border: '1px solid #ddd' }} onClick={this.resetChallenge}>Cancel</button> : null}
              </div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px', fontWeight: 700, borderBottom: '1px solid #f0f0f0' }}>Challenges</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead><tr style={{ background: '#fafafa' }}><th style={th}>Title</th><th style={th}>Metric</th><th style={th}>Target</th><th style={th}>Reward</th><th style={th}>Status</th><th style={th}></th></tr></thead>
                <tbody>
                  {challenges.length === 0 && <tr><td style={td} colSpan={6}>No challenges yet.</td></tr>}
                  {challenges.map((c) => (
                    <tr key={c.id}>
                      <td style={td}>{c.title}</td><td style={td}>{c.metric}</td><td style={td}>{c.target}</td><td style={td}>{c.rewardText || '—'}</td>
                      <td style={td}><span style={{ color: c.status === 'active' ? TEAL : '#999', fontWeight: 600 }}>{c.status}</span></td>
                      <td style={td}>
                        <button style={{ ...btn(INK), padding: '4px 10px', marginRight: 6 }} onClick={() => this.editChallenge(c)}>Edit</button>
                        <button style={{ ...btn(c.status === 'active' ? RED : TEAL), padding: '4px 10px' }} onClick={() => this.toggleChallenge(c)}>{c.status === 'active' ? 'Close' : 'Activate'}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {this.state.tab === 'workers' && (
          <div>
            <div style={card}>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>Look up a worker&apos;s portfolio</div>
              <input style={{ ...input, width: 340 }} placeholder="Worker ID (UUID)" value={this.state.wId}
                     onChange={(e) => this.setState({ wId: e.target.value.trim() })} />
              <button style={btn(INK)} disabled={this.state.busy} onClick={this.lookupWorker}>Load</button>
            </div>
            {this.state.wLoaded && (() => {
              const pf = this.props.portfolio && this.props.portfolio.getWorkerPortfolioAdmin;
              if (!pf || pf.status !== 200) return <div style={card}>Worker not found.</div>;
              return (
                <div style={card}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{pf.workerName || '(no name)'} — <span style={{ color: TEAL }}>{pf.tier}</span></div>
                  <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>{pf.count} / 20 photos</div>
                  {/* ADMIN-ONLY management metrics — acceptance / cancellation are no longer part of the tier. */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                    {[
                      { l: 'Acceptance rate', v: pf.acceptanceRate == null ? '—' : `${Math.round(pf.acceptanceRate * 100)}%`, sub: `${pf.acceptedJobs || 0} accepted · ${pf.declinedJobs || 0} declined` },
                      { l: 'Cancellation rate', v: pf.cancellationRate == null ? '—' : `${Math.round(pf.cancellationRate * 100)}%`, sub: `${pf.cancelledJobs || 0} cancelled · ${pf.completedJobs || 0} completed` },
                      { l: 'Completed jobs', v: String(pf.completedJobs || 0), sub: 'lifetime' },
                    ].map((m) => (
                      <div key={m.l} style={{ minWidth: 150, background: '#fafafa', border: '1px solid #eee', borderRadius: 10, padding: '10px 12px' }}>
                        <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>{m.l}</div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: INK }}>{m.v}</div>
                        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{m.sub}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: '#aaa', marginBottom: 12 }}>Acceptance &amp; cancellation are management metrics only — they do not affect the worker&apos;s tier.</div>
                  {(pf.result || []).length === 0 ? <div style={{ color: '#999' }}>No portfolio photos.</div> : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                      {pf.result.map((p) => (
                        <div key={p.id} style={{ width: 120 }}>
                          <img src={p.imageUrl} alt={p.caption || ''} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }} />
                          {p.caption ? <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{p.caption}</div> : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {this.state.tab === 'instant' && (() => {
          const ip = this.state.ipCfg;
          const payouts = (this.props.ipList && this.props.ipList.getInstantPayouts && this.props.ipList.getInstantPayouts.result) || [];
          const num = (label, key, hint) => (
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 13, color: '#444', display: 'inline-block', width: 230 }}>{label}</label>
              <input style={{ ...input, width: 110 }} value={ip[key]} onChange={(e) => this.setIp(key, e.target.value)} />
              {hint ? <span style={{ fontSize: 12, color: '#999' }}>{hint}</span> : null}
            </div>
          );
          return (
            <div>
              <div style={card}>
                <div style={{ fontWeight: 700, marginBottom: 12 }}>Instant Pay rules</div>
                {!ip ? <div>Loading…</div> : (
                  <div>
                    {num('Unlock after (completed jobs)', 'threshold', 'e.g. 50')}
                    {num('Fee charged to worker (%)', 'feePercent', 'e.g. 3')}
                    {num('Minimum per cash-out ($)', 'minCashout', 'e.g. 20')}
                    {num('Daily limit per worker ($)', 'dailyLimit', 'we front this — caps our exposure')}
                    {num('Max cash-outs per day', 'maxPerDay', 'e.g. 1')}
                    <div style={{ marginTop: 12 }}>
                      <button style={btn(RED)} disabled={this.state.busy} onClick={this.saveIp}>Save Instant Pay rules</button>
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>Workers cash out earned-but-not-yet-settled funds to a debit card. We front the advance and recoup it from the client&apos;s settled payment; refunds claw back from future payouts.</div>
                  </div>
                )}
              </div>

              <div style={card}>
                <div style={{ fontWeight: 700, marginBottom: 10 }}>Suspend / re-enable a worker&apos;s Instant Pay</div>
                <input style={{ ...input, width: 340 }} placeholder="Worker ID (UUID)" value={this.state.sId} onChange={(e) => this.setState({ sId: e.target.value.trim() })} />
                <button style={btn(RED)} disabled={this.state.busy} onClick={() => this.toggleSuspend(true)}>Suspend</button>
                <button style={btn(TEAL)} disabled={this.state.busy} onClick={() => this.toggleSuspend(false)}>Re-enable</button>
                <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>Note: an open dispute on a worker already auto-suspends their Instant Pay.</div>
              </div>

              <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ padding: '10px 14px', fontWeight: 700, borderBottom: '1px solid #f0f0f0' }}>Recent cash-outs</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead><tr style={{ background: '#fafafa' }}><th style={th}>Worker</th><th style={th}>Gross</th><th style={th}>Fee</th><th style={th}>Net</th><th style={th}>Jobs</th><th style={th}>Status</th><th style={th}>When</th></tr></thead>
                  <tbody>
                    {payouts.length === 0 && <tr><td style={td} colSpan={7}>No cash-outs yet.</td></tr>}
                    {payouts.map((r) => (
                      <tr key={r.id}>
                        <td style={td}>{r.workerName || r.partnerId}</td>
                        <td style={td}>${(r.gross || 0).toFixed(2)}</td>
                        <td style={td}>${(r.fee || 0).toFixed(2)}</td>
                        <td style={td}><b>${(r.net || 0).toFixed(2)}</b></td>
                        <td style={td}>{r.jobsCount}</td>
                        <td style={td}><span style={{ color: r.status === 'paid' ? TEAL : RED, fontWeight: 600 }}>{r.status}</span></td>
                        <td style={td}>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
        {this.state.tab === 'onboarding' && (() => {
          const ob = this.state.obCfg;
          const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          if (!ob) return <div style={card}>Loading…</div>;
          return (
            <div style={card}>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>Zoom onboarding (optional — never blocks workers from going online)</div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <input type="checkbox" checked={!!ob.enabled} onChange={(e) => this.setState({ obCfg: { ...ob, enabled: e.target.checked } })} />
                <span style={{ fontWeight: 600 }}>Enable onboarding banner &amp; sessions</span>
              </label>
              <div style={{ fontWeight: 700, margin: '6px 0' }}>Weekly sessions</div>
              {ob.sessions.length === 0 && <div style={{ color: '#999', marginBottom: 8 }}>No sessions yet — add two (e.g. Tuesday &amp; Thursday).</div>}
              {ob.sessions.map((s, i) => (
                <div key={i} style={{ borderTop: '1px solid #f0f0f0', padding: '10px 0' }}>
                  <input style={{ ...input, width: 240 }} placeholder="Title" value={s.title || ''} onChange={(e) => this.setObSession(i, 'title', e.target.value)} />
                  <select style={input} value={s.day || 'Tuesday'} onChange={(e) => this.setObSession(i, 'day', e.target.value)}>
                    {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <input style={{ ...input, width: 90 }} placeholder="15:00" value={s.time || ''} onChange={(e) => this.setObSession(i, 'time', e.target.value)} />
                  <input style={{ ...input, width: 70 }} placeholder="ET" value={s.timezone || ''} onChange={(e) => this.setObSession(i, 'timezone', e.target.value)} />
                  <input style={{ ...input, width: 320 }} placeholder="Zoom link" value={s.zoomLink || ''} onChange={(e) => this.setObSession(i, 'zoomLink', e.target.value)} />
                  <button style={{ ...btn(RED), padding: '6px 10px' }} onClick={() => this.removeObSession(i)}>Remove</button>
                </div>
              ))}
              <div style={{ marginTop: 12 }}>
                <button style={{ ...btn('#fff'), color: INK, border: '1px solid #ddd' }} onClick={this.addObSession}>+ Add session</button>
                <button style={btn(RED)} disabled={this.state.busy} onClick={this.saveOb}>Save onboarding</button>
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>Workers see a dismissible banner with the next dates; tapping “Join” opens the Zoom and hides the banner. Time/timezone are shown to the worker; use a recurring Zoom meeting link.</div>
            </div>
          );
        })()}
      </div>
    );
  }
}

export default compose(
  withQuery(getWorkerTierConfig, { name: 'tierCfg', options: { fetchPolicy: 'network-only', ssr: false } }),
  withQuery(getWorkerChallenges, { name: 'challenges', options: { fetchPolicy: 'network-only', ssr: false } }),
  withQuery(getWorkerPortfolioAdmin, { name: 'portfolio', options: { variables: { workerId: '__none__' }, fetchPolicy: 'network-only', ssr: false } }),
  withQuery(getInstantPayConfig, { name: 'ipCfg', options: { fetchPolicy: 'network-only', ssr: false } }),
  withQuery(getInstantPayouts, { name: 'ipList', options: { fetchPolicy: 'network-only', ssr: false } }),
  withQuery(getOnboardingConfig, { name: 'obCfg', options: { fetchPolicy: 'network-only', ssr: false } }),
  withMutation(saveWorkerTierConfigM, { name: 'saveTierMut' }),
  withMutation(saveWorkerChallengeM, { name: 'saveChallengeMut' }),
  withMutation(saveInstantPayConfigM, { name: 'saveIpMut' }),
  withMutation(setInstantSuspendM, { name: 'suspendMut' }),
  withMutation(saveOnboardingConfigM, { name: 'saveObMut' }),
)(WorkerPerformance);
